// Frontend-side TypeScript model of the Visual JSON shape that the
// twasta runtime BI engine consumes. Mirrors the schema described in
// backend/app/services/visual_prompts.py.

// Lookup (F2 reference picker) + item_change (on-change fill rule) on a visual
// criterion reuse the SAME shapes the transaction designer uses, so the same
// editors (LookupEditor / ItemChangeRuleEditor / ItemChangeCodeEditor) and the
// shared runtime LookupModal drive them.
import type { EditMaskValue, ItemChange, LookupFilterRules, Validation } from '@twasta/page-runtime/txn/types'
import type { DisplayFormat } from '@twasta/page-runtime/txn'

export interface VisualColumn {
  COLID: number
  NAME: string
  DBTABLE: string
  DBNAME: string
  tableName?: string
  tableDisplayName?: string
  content?: string
  name?: string
  descr?: string
  type?: 'string' | 'number' | 'date'
  JAVATYPE?: string
  COLTYPE?: 'CHAR' | 'NUMBER' | 'DATE'
  NATIVETYPE?: string
  DBSIZE?: number | string
  EXPRESSIONTYPE?: 'C' | 'G'
  DEFAULTFUNCTION?: string
  FUNCTION?: string
  FEILD_TYPE?: string
  ALIGNMENT?: number
  alignment?: string
  WIDTH?: number
  FONT?: string
  FONTSIZE?: number
  FGCOLOR?: string
  BGCOLOR?: string
  BOLD?: number
  ITALIC?: number
  UNDERLINE?: number
  HIDDEN?: string | boolean
  KEY?: boolean
  CAPS?: boolean
  ADV_FORMAT?: string
  // ── Per-column rich display format + pivot aggregation (column formatting) ──
  /** Rich per-column display format (canonical). Simple number/date masks are
   *  authored here as { type:'number', … } / { type:'date', datePattern } via
   *  DisplayFormatEditor. Render precedence: display > FORMAT/PATTERN > raw;
   *  ADV_FORMAT conditions always style on top. */
  display?: DisplayFormat
  /** ProPivot client-side pivot aggregation (17 values — see visuals.md /
   *  ALL_AGGREGATIONS). Separate from FUNCTION, which remains the SQL aggregate
   *  / GROUP-BY shaping. Absent → falls back to WDR_AGG[FUNCTION]. Window /
   *  percent functions MUST live here, never in FUNCTION. */
  PIVOT_AGG?: string
  /** Positional direction for difference / %difference / runningtotals. */
  PIVOT_AXIS?: 'rows' | 'columns'
  /** Legacy number mask (IND/USA/EURO/9-mask) — render-time compat only. */
  FORMAT?: string
  /** Legacy date pattern — render-time compat only. */
  PATTERN?: string
  expression?: string
  value?: string
  groupName?: string
  StandardName?: string
  checked?: boolean
  [key: string]: unknown
}

/** One independently-sourced bound of a BETWEEN criterion (see
 *  `CriteriaRule.operands`). `value` is the literal / prompt default;
 *  `customValue` the system-variable token when source === 'variable'. */
export interface CriteriaOperand {
  source: 'prompt' | 'literal' | 'variable'
  value?: string
  customValue?: string
  promptLabel?: string
}

export interface CriteriaRule {
  id: string
  field: string
  DBNAME?: string
  tableName?: string
  COLTYPE: 'string' | 'number' | 'date'
  currentFieldType?: 'string' | 'number' | 'date'
  operator: string
  /** 'row_limit' → this rule feeds the query's ROW LIMIT (Top-N prompt),
   *  never the WHERE; its `field` is a prompt name (e.g. TOP_N), not a
   *  column (managed by the "Sort & Top N" panel). 'having' → the condition
   *  applies AFTER aggregation (function conditions — the engine emits it
   *  in the HAVING clause). */
  applyTo?: 'row_limit' | 'having'
  /** Function condition: a dialect-supported aggregate/statistical function
   *  (SUM/AVG/COUNT_DISTINCT/MEDIAN/…) applied to the rule's column — the
   *  engine spells it for the bound database and routes the condition to
   *  HAVING. */
  FUNCTION?: string
  /** Free-hand left-side expression (author-trusted), e.g. SUM(QTY * RATE).
   *  Takes precedence over field/FUNCTION; pair with applyTo:'having' when
   *  it aggregates. */
  expression?: string
  queryOption?: string
  prevOption?: string
  customValue?: string
  promptLabel?: string
  /** SQL mode: the `?.<name>` placeholder this rule configures, when `field`
   *  names the column it filters instead (the engine's deterministic link —
   *  visual_data_service matches promptVar, then field, then id). */
  promptVar?: string
  value?: string
  chipValue?: unknown[]
  // ── BETWEEN with independently-sourced bounds ──
  // When operator === 'between', the two bounds may each come from their OWN
  // source (e.g. TRAN_DATE >= <prompt> AND <= <fixed date> in one criterion).
  // The engine prefers `operands` over the legacy single-source chipValue
  // pair when present (bi_sql.source_sql.is_operand_between).
  operands?: [CriteriaOperand, CriteriaOperand]
  // ── Optional reference lookup (F2 picker) on this prompt criterion ──
  // Only meaningful when queryOption === 'P'. Mirrors ColumnDef's lookup
  // fields; the runtime VisualCriteriaForm shows an F2 button that opens the
  // shared LookupModal so the user can pick the filter value from a table.
  lookup?: string
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  lookup_sql?: string
  lookup_title?: string
  lookup_sql_data_col?: string
  // ── Optional checks on this prompt criterion ──
  // The transaction field's validation rules, run by the server on the
  // criteria before the visual / report runs (E stops it; W / P are shown and
  // it goes ahead); {form.X} reads another criterion. A prompt with no default
  // is already required (its SQL binds it), so there is no separate mandatory
  // flag here. docs/plans/criteria-mandatory-and-validations.md.
  validations?: Validation[]
  // ── Optional item_change (on-change rule) on this prompt criterion ──
  // When the user changes this criterion's value, fill/modify OTHER criteria
  // (by param name). Same union + return contract as a transaction column's
  // item_change. ('+ Add master' is intentionally disabled for criteria.)
  item_change?: ItemChange
  // ── Optional fixed dropdown (picklist) for this prompt criterion ──
  // When set, the runtime renders a <select> of these {data, display} options
  // instead of a free-text input (takes precedence over a lookup). The stored
  // value is the chosen option's `data`.
  options?: EditMaskValue[]
  [key: string]: unknown
}

export interface VisualOption {
  optionId: string
  optionName: string
  optionHeading: string
  dataType: 'boolean' | 'string' | 'number'
  defaultValue: string
  listOfValues: string
  allowChange: string
  optionType?: string
  // Conditional visibility in the designer, "otherOption:val1,val2" — the
  // option is only shown when otherOption's current value is one of the listed.
  showWhen?: string
}

export interface ColumnGroup {
  GroupID: number
  GroupName: string
  GroupDescription: string
  GroupIcon: string
  AllowedColumnTypes: 'ANY' | 'NUMBER' | 'DATE' | 'STRING'
  MinColumns: number
  MaxColumns: number | string
  StandardName: string
  COLUMNS: VisualColumn[]
}

export interface VisualLayout {
  ID: string
  VisualName: string
  VisualType: string
  VisualIcon: string
  DisplayOrder: string
  className: string
  linkOptions: string
  OutputType: string
  options: VisualOption[]
  ColumnGroups: ColumnGroup[]
  /** The heading shown over the visual. Blank = the menu entry's name (else
   *  the object name). */
  title?: string
  /** The line under the heading. Blank = the menu entry's description. */
  subtitle?: string
  /** Events on a time-series chart (docs/visual_events.md) — the layers'
   *  design; the engine normalises it and resolves it per run. */
  events?: import('@twasta/visual-engine').EventsBlock
}

/** A nested condition GROUP inside CRITERIA.query.rules — a set of criteria
 *  combined with the group's OWN and/or, rendered as `( a OR b )` by every
 *  engine (they all recurse on `{condition, rules}` nodes). Groups nest to
 *  any depth — a group's rules may themselves contain groups — so e.g.
 *  `(site = x OR (region = y AND tier = 1)) AND doc_date > ?` is authorable.
 *  `id` is designer-only (collapse state / React keys); engines ignore it. */
export interface CriteriaGroup {
  condition: 'and' | 'or'
  rules: CriteriaNode[]
  id?: string
  [key: string]: unknown
}

/** One entry of CRITERIA.query.rules: a leaf criterion or a nested group. */
export type CriteriaNode = CriteriaRule | CriteriaGroup

/** Group detection — the exact test every engine's WHERE walker uses. */
export function isCriteriaGroup(node: CriteriaNode): node is CriteriaGroup {
  return !!node && typeof node === 'object'
    && Array.isArray((node as CriteriaGroup).rules) && 'condition' in node
}

/** Flatten rules + groups (any nesting depth) into the leaf criteria, in
 *  depth-first walk order (the order the engines derive prompt names in). */
export function criteriaLeaves(nodes: CriteriaNode[]): CriteriaRule[] {
  const out: CriteriaRule[] = []
  for (const n of nodes || []) {
    if (isCriteriaGroup(n)) out.push(...criteriaLeaves(n.rules || []))
    else out.push(n)
  }
  return out
}

/** Resolve a group by its index path from the top-level rules (e.g. [2, 0] =
 *  first child group of the third top-level node). Returns null when the path
 *  no longer points at a group (stale index after a structural edit). */
export function criteriaGroupAtPath(nodes: CriteriaNode[], path: number[]): CriteriaGroup | null {
  let list = nodes
  let group: CriteriaGroup | null = null
  for (const idx of path) {
    const node = list[idx]
    if (!node || !isCriteriaGroup(node)) return null
    group = node
    list = node.rules
  }
  return group
}

/** One `SQLMODEL.ORDER_BY` entry — orders on a model column (its NAME alias
 *  or DBNAME) or a raw TABLE.COL. The engine emits the dialect-correct SQL. */
export interface OrderByEntry {
  column: string
  direction: 'ASC' | 'DESC'
}

export interface VisualSqlModel {
  SQLModel: { COLUMNS: { COLUMN: VisualColumn[] }[] }
  CRITERIA: { query: { rules: CriteriaNode[]; condition: 'and' | 'or' } }
  /** Result ordering (Top-N ranking). Applied after GROUP BY/HAVING. */
  /**
   * The author pinned the row limit: a viewer may not raise it.
   *
   * Absent (the default) means raisable, so a visual authored before this
   * existed keeps behaving as a list whose reader may ask for more.
   */
  ROW_LIMIT_FIXED?: boolean
  ORDER_BY?: OrderByEntry[]
  /** Fixed row cap (Top-N). An applyTo:'row_limit' criteria rule (the
   *  prompt-driven form) overrides this when present. The engine emits the
   *  bound database's syntax (LIMIT / FETCH FIRST / TOP) itself. */
  ROW_LIMIT?: number
  /** The visual's calculations (visual/calculations.ts): formulas over the
   *  data source's fields. A PLACED one is also an ordinary column carrying
   *  `expression` + `CALC`; this list keeps them all, placed or not. */
  CALCULATIONS?: Array<{ key: string; label: string; formula: string; when: 'total' | 'row' }>
  VISUAL_NAME: string
  SCHEMA_NAME: string
  /** Business Area this visual is based on ('' = Default — no area needed;
   *  the visual reads the project DB / its own schema as before). The area
   *  supplies the dataset (its optional schema, else its own tables/joins)
   *  and the data-source connection. */
  BUSINESS_AREA?: string
  SCHEMA_DESCR: string
  SCHEMA_TYPE: string
  OUTPUT_TYPE: string
  DATABASE_TYPE: string
  DATABASE_DETAIL: { DATABASE_NAME: string; TYPE: number; CONNECTION_ID?: string }
}

export type DataSourceMode = 'sql' | 'sqlmodel'

// ── Visual Schema (file at Metadata_Model/visual_schemas/<SCHEMA_NAME>.json) ──
// Reused across multiple Schema-mode visuals that pull from the same dataset.

export interface SchemaTableColumn {
  DBNAME: string
  /** Dataset-unique name for this column, `TABLE_DBNAME` (auto-filled on
   *  import, editable). It is what a schema-mode visual uses as its SELECT
   *  alias, so two tables that share a column name (ITEM.DESCR + SITE.DESCR)
   *  never collide in the query or in the result keys. Absent on columns
   *  authored before this existed — those keep aliasing by DBNAME. */
  UNIQUE_NAME?: string
  DISPLAY_NAME?: string
  COLTYPE?: 'CHAR' | 'NUMBER' | 'DATE'
  DBSIZE?: number | string
  NATIVETYPE?: string
  JAVATYPE?: string
  DEFAULTFUNCTION?: string
  ROW_SECURITY?: boolean
  COL_SECURITY?: boolean
  HIDDEN?: boolean
  [key: string]: unknown
}

export interface SchemaTable {
  TABLE_NAME: string
  TABLE_DISPLAY_NAME?: string
  ALIAS?: string
  /** Enforced filter: SQL predicate AND-ed into the WHERE automatically
   *  whenever this table is queried (everyone, alongside ROW_SECURITY).
   *  May use ?.@Tokens, :current_* tokens and :name project variables. */
  CONDITION?: string
  COLUMNS: SchemaTableColumn[]
  [key: string]: unknown
}

export interface SchemaJoin {
  JOIN_ID: string
  LEFT_TABLE: string
  RIGHT_TABLE: string
  JOIN_TYPE: 'INNER' | 'LEFT' | 'RIGHT' | 'FULL' | string
  JOIN_CONDITION: string
  /** Plain-language relationship note. A join with a DESCRIPTION and no
   *  JOIN_CONDITION is "description-only": the NL query generator works out
   *  the join from it (the deterministic SQL builder ignores it). */
  DESCRIPTION?: string
  /** Edit the ON condition as a raw expression (functions / extra predicates)
   *  instead of picking column pairs. Presentation-only: JOIN_CONDITION stays
   *  the single source of truth. */
  FREE_TEXT?: boolean
  [key: string]: unknown
}

/** Persisted diagram node positions, keyed by TABLE_NAME. Absent tables fall
 *  back to an auto-layout. Carried through load/save via the index signatures. */
export type JoinLayout = Record<string, { x: number; y: number }>


export interface SchemaRowSecurityPolicy {
  POLICY_ID: string
  /** Table the filter applies to; the special value "ALL" applies it to
   *  every table in the query (optionally gated by WHEN_COLUMN_EXISTS). */
  TABLE_NAME: string
  /** SQL predicate. May use ?.@Tokens and the security tokens
   *  :current_user_id / :current_user_email / :current_date /
   *  :current_timestamp (bound server-side, never inlined). */
  CONDITION: string
  APPLIES_TO_ROLES: string[]
  /** Apply only when the table actually has this column (insight-db port). */
  WHEN_COLUMN_EXISTS?: string
  /** Inherit the parent table's filter: when the parent is absent from the
   *  query the condition is enforced through EXISTS(parent JOIN_CONDITION). */
  INHERIT_FROM?: string
  JOIN_CONDITION?: string
}

export type ColumnMaskType =
  | '' | 'partial' | 'replace' | 'null' | 'hash' | 'range' | 'custom' | 'disallow'

export interface MaskRange {
  min?: number | string
  max?: number | string
  display: string
}

export interface SchemaColumnSecurityPolicy {
  POLICY_ID: string
  TABLE_NAME: string
  COLUMN_NAME: string
  ACTION: 'HIDE' | 'MASK' | string
  APPLIES_TO_ROLES: string[]
  /** How a MASK anonymizes (insight-db port): partial (format-preserving,
   *  keeps last 4), replace, null, hash, range, custom SQL, or disallow
   *  (rejects any query touching the column). Empty = legacy '********'. */
  MASK_TYPE?: ColumnMaskType
  /** partial: e.g. XXX-XX-#### or ****@domain.com */
  PATTERN?: string
  /** replace: the fixed text shown instead of the value */
  REPLACEMENT?: string
  /** custom: SQL expression ({col} = the column reference) */
  SQL?: string
  /** range: value buckets */
  RANGES?: MaskRange[]
}

// ── Business model folders (Facts / Dimensions / custom) ──────────────
// The schema designer lets users drag source columns into business-friendly
// folders. A folder column REFERENCES a source column (TABLE_NAME.DBNAME) and
// carries presentation metadata (heading, default aggregation, description).
export interface SchemaGroupColumn {
  TABLE_NAME: string          // source table the column comes from
  DBNAME: string              // source column name
  /** Copy of the source column's UNIQUE_NAME (`TABLE_DBNAME`) — the SELECT
   *  alias a visual built on this schema uses. Kept in step with
   *  TABLES[].COLUMNS: edit it there (or via this folder column, which writes
   *  through). Absent → the visual aliases by DBNAME, as before. */
  UNIQUE_NAME?: string
  DISPLAY_NAME?: string       // business heading shown in pickers
  COLTYPE?: 'CHAR' | 'NUMBER' | 'DATE'
  DEFAULTFUNCTION?: string    // SUM/COUNT/AVG/MIN/MAX for measures, '' otherwise
  DESCRIPTION?: string
  /** Rich per-column display format (same shape / editor the Visual designer
   *  uses). Authored on the right-hand folder column here; carried onto the
   *  materialised VisualColumn.display so a visual built on this schema inherits
   *  the formatting the moment the column is selected. */
  display?: DisplayFormat
  [key: string]: unknown
}

export interface SchemaColumnGroup {
  GROUP_ID: string            // stable id, e.g. 'G001'
  GROUP_NAME: string          // 'Facts', 'Dimensions', or custom
  GROUP_DISPLAY_NAME?: string // label shown when picking columns in a visual (defaults to GROUP_NAME)
  GROUP_ICON?: string         // emoji shown on the folder header
  ALLOWED_TYPES?: 'ANY' | 'NUMBER' | 'DATE' | 'STRING'  // advisory type filter
  DESCRIPTION?: string
  COLUMNS: SchemaGroupColumn[]
}

export interface VisualSchema {
  SCHEMA_NAME: string
  SCHEMA_DESCR: string
  SCHEMA_TYPE: 'S' | string
  CONNECTION_ID?: string
  /** The fact/anchor table — the query builder starts the FROM here when it
   *  participates, so joins fan out from the fact. */
  MAIN_TABLE?: string
  TABLES: SchemaTable[]
  JOINS: SchemaJoin[]
  /** Draggable join-diagram node positions (keyed by TABLE_NAME). */
  JOIN_LAYOUT?: JoinLayout
  ROW_SECURITY?: SchemaRowSecurityPolicy[]
  COLUMN_SECURITY?: SchemaColumnSecurityPolicy[]
  // Business-friendly column organization (Facts / Dimensions / custom folders)
  COLUMN_GROUPS?: SchemaColumnGroup[]
}

export interface VisualSchemaFile {
  SCHEMA: VisualSchema
}

export interface VisualModel {
  // Which side is the source of truth for columns + criteria.
  //   'sql'      → SOURCE_SQL drives both columns (SELECT aliases) and
  //                criteria (?.var placeholders embedded in WHERE).
  //   'sqlmodel' → SQLMODEL.SQLModel.COLUMNS and SQLMODEL.CRITERIA drive the
  //                visual; the runtime BI engine generates SQL from them.
  // The opposite section may still be filled in (the LLM often emits both
  // for safety), but the editor only edits the side that matches the mode.
  DATA_SOURCE_MODE: DataSourceMode
  SQLMODEL: VisualSqlModel
  LAYOUT_DATA: { visualLayout: VisualLayout; layoutdata: Record<string, unknown> }
  SOURCE_SQL: string
}
